import { useState, useEffect } from 'react';

export const useAudioPlayer = () => {
  const [currentlyPlaying, setCurrentlyPlaying] = useState(null);
  const [audioElement, setAudioElement] = useState(null);
  const [playbackTimes, setPlaybackTimes] = useState({});
  const [durations, setDurations] = useState({});
  const [currentTimes, setCurrentTimes] = useState({});

  const handlePlayPause = (episode) => {
    // Get the first audio file URL from the episode
    const audioUrl = episode.audio_files && episode.audio_files.length > 0 
      ? episode.audio_files[0].audio_url 
      : null;

    if (!audioUrl) {
      console.warn('No audio file available for this episode');
      return;
    }

    // If this episode is currently playing, pause it
    if (currentlyPlaying === episode.id) {
      if (audioElement) {
        // Store current playback position before pausing
        setPlaybackTimes(prev => ({
          ...prev,
          [episode.id]: audioElement.currentTime
        }));
        audioElement.pause();
        setAudioElement(null);
        setCurrentlyPlaying(null);
      }
    } else {
      // Stop any currently playing audio
      if (audioElement) {
        // Store current playback position before switching
        setPlaybackTimes(prev => ({
          ...prev,
          [currentlyPlaying]: audioElement.currentTime
        }));
        audioElement.pause();
      }

      // Create new audio element and play
      const newAudio = new Audio(audioUrl);
      
      // Set up event listeners
      newAudio.addEventListener('loadedmetadata', () => {
        setDurations(prev => ({
          ...prev,
          [episode.id]: newAudio.duration
        }));
      });

      newAudio.addEventListener('timeupdate', () => {
        setCurrentTimes(prev => ({
          ...prev,
          [episode.id]: newAudio.currentTime
        }));
      });

      newAudio.addEventListener('ended', () => {
        setCurrentlyPlaying(null);
        setAudioElement(null);
        // Reset playback position when ended
        setPlaybackTimes(prev => ({
          ...prev,
          [episode.id]: 0
        }));
      });
      
      newAudio.addEventListener('error', (e) => {
        console.error('Audio playback error:', e);
        setCurrentlyPlaying(null);
        setAudioElement(null);
      });

      // Resume from stored position or start from beginning
      const resumePosition = playbackTimes[episode.id] || 0;
      newAudio.currentTime = resumePosition;
      
      newAudio.play();
      setAudioElement(newAudio);
      setCurrentlyPlaying(episode.id);
    }
  };

  const handleRewind = () => {
    if (audioElement) {
      audioElement.currentTime = Math.max(0, audioElement.currentTime - 10);
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (audioElement) {
        audioElement.pause();
        setAudioElement(null);
      }
    };
  }, [audioElement]);

  return {
    currentlyPlaying,
    audioElement,
    playbackTimes,
    durations,
    currentTimes,
    handlePlayPause,
    handleRewind
  };
};
